import React, { useState } from 'react';
import { App, Button, Col, Drawer, Form, Input, Row, Spin } from 'antd';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../reducers';
import { getSelectedItems } from '../../../selectors/catalogue/catalogueSelector';
import {
  fetchDefaultNotes,
  fetchDefaultQualifiers,
  fetchRankedPositions,
} from '../../../actions/items/application/fetch';
import { updateItem } from '../../../actions/catalogue/catalogue/update';
import { Item } from '../../../../types/item';
import { ApplicationNote, Qualifier, RankedPositions } from '../../../../types/application';
import { StandardResource } from '../../../../types/resources';
import { DefaultValue } from '../../../../types/brand_settings';
import FormInput from '../../global/Forms/FormInput';
import ApplicationPosition from './ApplicationPosition';
import DefaultQualifierContainer from '../../../containers/application/DefaultQualifierContainer';
import {
  updateDefaultNotes,
  updateDefaultQualifiers,
} from '../../../actions/items/application/update';
import { intercomEvent } from '../../../utils/IntercomUtils';
import IntercomFormEventHandler from '../../global/page/IntercomFormEventHandler';
import { brandAccDefaultValues } from '../../../selectors/default_values/defaultValuesSelector';

type ApplicationDefaultsFormProps = {
  mfrLabel: string;
  qty: number | null;
  position: number | null;
  overwrite: number;
  defaultData: {
    defaultQualifiers: (Qualifier & { changed?: boolean })[];
    defaultNotes: ApplicationNote[];
  };
};

type ApplicationDefaultsDrawerProps = {
  visible: boolean;
  onClose: () => void;
};

const ApplicationDefaultsDrawer: React.FC<ApplicationDefaultsDrawerProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const dispatch = useDispatch();

  const {
    item,
    fetchingDefaultQualifiers,
    defaultQualifiers,
    defaultNotes,
    rankedPositions,
    positionResources,
    defaultValues,
  } = useSelector(
    (state: ApplicationState) => ({
      item: getSelectedItems(state)[0] as Item,
      fetchingDefaultQualifiers: state.items.application.fetchingDefaultQualifiers as boolean,
      defaultQualifiers: state.items.application.defaultQualifiers as Qualifier[],
      defaultNotes: state.items.application.defaultNotes as ApplicationNote[],
      rankedPositions: state.items.application.rankedPositions as RankedPositions[],
      positionResources: state.resources.data.vehicle.positions as StandardResource[],
      defaultValues: brandAccDefaultValues(state) as DefaultValue[],
    }),
    shallowEqual
  );

  React.useEffect(() => {
    if (item.id) {
      dispatch(fetchDefaultQualifiers(item.id));
      dispatch(fetchDefaultNotes(item.id));
    }
  }, [dispatch, item.id]);

  React.useEffect(() => {
    // item.category is included in extended item data, so we have to check for it
    if (item.category?.id) dispatch(fetchRankedPositions(item.category.id));
  }, [dispatch, item.id, item.category]);

  const showPartTypeId = defaultValues.find(d => d.id === 11)?.value === '1';

  const validationSchema = Yup.object().shape({
    mfrLabel: Yup.string(),
    qty: Yup.number()
      .typeError(t('validation:invalidNumber'))
      .positive(t('validation:positiveNumber')),
  });

  const formMethods = useForm<ApplicationDefaultsFormProps>({
    defaultValues: {
      mfrLabel: item.default_mfr_label || '',
      qty: item.default_application_qty,
      position: item.default_position_id,
      overwrite: item.default_overwrite,
      defaultData: {
        defaultQualifiers,
        defaultNotes,
      },
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'onTouched',
  });

  const { handleSubmit, formState, reset } = formMethods;
  const { isDirty } = formState;

  React.useEffect(() => {
    if (!visible)
      reset({
        mfrLabel: item.default_mfr_label || '',
        qty: item.default_application_qty,
        position: item.default_position_id,
        overwrite: item.default_overwrite,
        defaultData: {
          defaultQualifiers,
          defaultNotes,
        },
      });
  }, [item, defaultQualifiers, defaultNotes, reset, visible]);

  const handleClose = () => {
    if (isDirty) {
      modal.confirm({
        title: t('common:closeWarningTitle'),
        icon: <ExclamationCircleOutlined />,
        onOk() {
          onClose();
        },
      });
    } else onClose();
  };

  const onSubmit = async (values: ApplicationDefaultsFormProps) => {
    intercomEvent('viewed-all-product', {
      action: 'item-saved',
      location: 'application_default_values',
      part_number: item?.part_number,
      brand_code: item?.brand_code,
    });

    const {
      mfrLabel,
      qty,
      position,
      overwrite,
      defaultData: { defaultQualifiers: updateQualifiers, defaultNotes },
    } = values;

    if (
      mfrLabel !== item.default_mfr_label ||
      qty !== item.default_application_qty ||
      position !== item.default_position_id ||
      overwrite !== item.default_overwrite
    ) {
      let updatedItem = { ...item };

      if (mfrLabel !== item.default_mfr_label) {
        updatedItem = { ...updatedItem, default_mfr_label: mfrLabel };
      }
      if (qty !== item.default_application_qty) {
        updatedItem = { ...updatedItem, default_application_qty: Number(qty) };
      }
      if (position !== item.default_position_id) {
        updatedItem = { ...updatedItem, default_position_id: position };
      }
      if (overwrite !== item.default_overwrite) {
        updatedItem = { ...updatedItem, default_overwrite: overwrite };
      }

      dispatch(updateItem(item.id, updatedItem));
    }

    const update = updateQualifiers.map((qualifier: Qualifier) => ({
      id: qualifier.qualifier_id,
      values: qualifier.values,
      record_number: qualifier.record_number,
    }));
    await Promise.all([
      dispatch(updateDefaultQualifiers(item.id, update)),
      dispatch(updateDefaultNotes(item.id, defaultNotes)),
    ]);
    onClose();
  };

  const form = () => {
    return (
      <FormProvider {...formMethods}>
        <Form
          layout="vertical"
          className="application__defaults-form"
          onFinish={handleSubmit(onSubmit)}
        >
          <Row>
            <Col span={24}>
              <Form.Item label={t('application:partType')}>
                <Input
                  placeholder={
                    item.category && item.category.id
                      ? `${item.category.level1_name} | ${item.category.level2_name} | ${
                          item.category.level3_name
                        }${showPartTypeId && item.part_type_id ? ` (${item.part_type_id})` : ''}`
                      : t('application:noPartType')
                  }
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20} align="top">
            <Col span={12}>
              <FormInput name="mfrLabel" label={t('application:mfrLabel')} useHookForm />
            </Col>
            <Col span={3}>
              <FormInput name="qty" label={t('application:qty')} useHookForm />
            </Col>
            <Col span={9}>
              <Form.Item label={t('application:position')}>
                <Controller
                  name="position"
                  control={formMethods.control}
                  render={({ field }) => (
                    <ApplicationPosition
                      className="application__overview-position-qty"
                      value={field.value || undefined}
                      rankedPositions={rankedPositions}
                      positionResources={positionResources}
                      onChange={(id: number | null) => {
                        field.onChange(id);
                      }}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* 
              <FormRadio
                name="overwrite"
                values={[
                  { id: 0, name: t('application:addToExisting').toString() },
                  { id: 1, name: t('application:overwriteExisting').toString() },
                ]}
                label={t('application:overwriteLabel')}
                buttonOptions
              /> */}

          <div className="ant-form-item-label application__default-qualifiers-label">
            <label>{t('application:defaults.qualifier')}</label>
          </div>
          <div className="application__default-qualifiers-row">
            <Controller
              name="defaultData"
              control={formMethods.control}
              render={({ field }) => (
                <DefaultQualifierContainer
                  itemId={item.id}
                  defaultQualifiers={field.value.defaultQualifiers}
                  deleteDefaultQualifier={(id: number, recordNumber: number) => {
                    const qualifiers = field.value.defaultQualifiers.filter(
                      q => !(q.qualifier_id === id && q.record_number === recordNumber)
                    );
                    field.onChange({ ...field.value, defaultQualifiers: qualifiers });
                  }}
                  updateDefaultQualifier={(qualifier: Qualifier) => {
                    const updated = { ...qualifier, changed: true };
                    const qualifiers = field.value.defaultQualifiers.find(
                      ({ qualifier_id }) => updated.qualifier_id === qualifier_id
                    )
                      ? field.value.defaultQualifiers.map(q =>
                          q.qualifier_id === updated.qualifier_id &&
                          q.record_number === updated.record_number
                            ? updated
                            : q
                        )
                      : [...field.value.defaultQualifiers, updated];

                    field.onChange({ ...field.value, defaultQualifiers: qualifiers });
                  }}
                  duplicateDefaultQualifier={(qualifier: Qualifier) => {
                    const defaultQualifiers = [...field.value.defaultQualifiers];
                    let index = 0;
                    defaultQualifiers.forEach((q, i) => {
                      if (q.qualifier_id === qualifier.qualifier_id) index = i;
                    });

                    const updatedQualifiers = [...defaultQualifiers];
                    updatedQualifiers.splice(index + 1, 0, qualifier);

                    field.onChange({ ...field.value, defaultQualifiers: updatedQualifiers });
                  }}
                  clearTempState={() => {
                    const updated = field.value.defaultQualifiers.map(q => ({ ...q, temp: false }));
                    field.onChange({ ...field.value, defaultQualifiers: updated });
                  }}
                  notes={field.value.defaultNotes}
                  addNote={(noteValue?: string) => {
                    const recordNumber = field.value.defaultNotes.length
                      ? Math.max(...field.value.defaultNotes.map(n => n.record_number))
                      : 1;
                    const notes = [
                      ...field.value.defaultNotes,
                      { record_number: (recordNumber || 0) + 1, note: noteValue || '' },
                    ];
                    field.onChange({ ...field.value, defaultNotes: notes });
                  }}
                  removeNote={(recordNumber: number) => {
                    const notes = field.value.defaultNotes.filter(
                      n => n.record_number !== recordNumber
                    );
                    field.onChange({ ...field.value, defaultNotes: notes });
                  }}
                  updateNote={(recordNumber: number, noteValue: string) => {
                    const note = field.value.defaultNotes.find(
                      n => n.record_number === recordNumber
                    );
                    const notes = note
                      ? field.value.defaultNotes.map(n =>
                          n.record_number === recordNumber ? { ...n, note: noteValue } : n
                        )
                      : [
                          ...field.value.defaultNotes,
                          { record_number: recordNumber, note: noteValue },
                        ];
                    field.onChange({ ...field.value, defaultNotes: notes });
                  }}
                />
              )}
            />
          </div>
          <div className="application__defaults-form-buttons">
            <Row gutter={20}>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button
                  className="application__defaults-cancel-button"
                  onClick={() => handleClose()}
                >
                  {t('common:cancel')}
                </Button>
                <Button
                  className="application__defaults-submit-button"
                  disabled={!formState.isDirty || formState.isSubmitting}
                  type="primary"
                  htmlType="submit"
                  loading={formState.isSubmitting}
                >
                  {t('common:save')}
                </Button>
              </Col>
            </Row>
          </div>

          <IntercomFormEventHandler
            segment="application_default_values"
            partNr={item.part_number}
            brandCode={item.brand_code}
            useHookForm
          />
        </Form>
      </FormProvider>
    );
  };

  return (
    <Drawer
      className="application__defaults-drawer"
      title={t('application:defaults.title')}
      width="60%"
      open={visible}
      onClose={handleClose}
      destroyOnClose
      style={{ height: '100%' }}
    >
      {fetchingDefaultQualifiers && <Spin />}
      {!fetchingDefaultQualifiers && <div className="application__defaults">{form()}</div>}
    </Drawer>
  );
};

export default ApplicationDefaultsDrawer;
